/**
 * Local, no-network backend — offline/dev parity for the tiered {@link GameServices}
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md: "localBackend.ts — offline/dev parity
 * (trusted, single-player)"). Used when Firebase is OFF (the current test run) or
 * when config is missing. The rest of the app is identical regardless of backend.
 *
 * TRUSTED parity model (the inverse of the Firebase backend, which never trusts
 * the client): with no server to re-simulate, this backend re-runs the
 * deterministic core sim LOCALLY to compute the authoritative score from a
 * {@link RunRecord} — the SAME {@link runSimulation} the Cloud Function uses — so
 * scoring matches online play exactly. Everything else (profile, stickers, tokens,
 * wheel) is an in-memory simulation of the server economy so the full UI is
 * exercisable offline.
 *
 *   - uid:         generated once, persisted in localStorage.
 *   - tier:        `named` for local dev — offline play exercises the FULL UI
 *                  (profile/stickers/wheel), unlike the anon-only Firebase tease.
 *   - high score:  localStorage (the player's best authoritative score).
 *   - leaderboard: a few seeded demo entries merged with the player's runs.
 *   - presence:    static count of 1 (just you).
 *   - economy:     in-memory profile + token wallet + owned stickers + a seeded
 *                  sticker catalog and prize wheel; spins/buys debit locally.
 *
 * NOTE: this file is a service backend, not core — `Date.now`/`Math.random` are
 * fine HERE (the determinism rule binds `core/`, not the data layer). Scoring,
 * though, defers to the pure core sim so it stays reproducible.
 */
import type { AttackFamily, RunRecord } from '../core/types';
import { runSimulation } from '../core/world';
import { scoreFromElapsedMs } from '../core/simulation';
import { levelForXp } from '../core/xp';
import { newlyUnlockedAchievements } from '../core/achievements';
import type { Rarity } from '../core/economy';
import type {
  AccountTier,
  CurrentUser,
  GameServices,
  RunReward,
  ScoreEntry,
  ServicesMode,
  SubmitRunResult,
} from './types';
import type {
  AdminMetrics,
  EventClaimResult,
  EventDoc,
  GameId,
  NetworkStats,
  NewsItem,
  PlayerGameStats,
  ProfileTier,
  RunSummary,
  RuntimeConfig,
  StickerCatalogItem,
  UserCard,
  UserProfile,
  WheelConfig,
  WheelResult,
  WheelSlot,
} from './dataModel';
import {
  DEFAULT_GAME_ID,
  NETWORK_RECENT_WINDOW,
  emptyNetworkStats,
  emptyPlayerGameStats,
  replayStarQuota,
  savedReplayQuota,
} from './dataModel';

const UID_KEY = 'barrage.uid';
const HIGH_KEY = 'barrage.highscore';
const BOARD_KEY = 'barrage.leaderboard';

/**
 * localStorage key for a game's personal-best score. The DEFAULT game ('barrage')
 * keeps the LEGACY un-suffixed key so an existing single-game install's best
 * carries over unchanged; any future game gets a `barrage.highscore.{gameId}` key.
 */
function highKeyFor(gameId: GameId): string {
  return gameId === DEFAULT_GAME_ID ? HIGH_KEY : `${HIGH_KEY}.${gameId}`;
}

/**
 * localStorage key for a game's personal board row. Same backward-compat rule as
 * {@link highKeyFor}: the default game reuses the legacy `barrage.leaderboard` key.
 */
function boardKeyFor(gameId: GameId): string {
  return gameId === DEFAULT_GAME_ID ? BOARD_KEY : `${BOARD_KEY}.${gameId}`;
}

/** Clearly-labelled demo entries so the leaderboard UI has something to show. */
const SEED: ScoreEntry[] = [
  { uid: 'seed-1', name: 'AcePilot (demo)', score: 4200, createdAt: 0 },
  { uid: 'seed-2', name: 'Nova (demo)', score: 3100, createdAt: 0 },
  { uid: 'seed-3', name: 'Vortex (demo)', score: 2450, createdAt: 0 },
  { uid: 'seed-4', name: 'Comet (demo)', score: 1600, createdAt: 0 },
];

/** A tiny seeded sticker catalog so the shop/collection UI is browsable offline. */
const SEED_CATALOG: StickerCatalogItem[] = [
  {
    id: 'starter-common',
    title: 'Pixel Pal',
    desc: 'A friendly starter sticker.',
    type: 'secret',
    category: 'starter',
    series: 's1',
    num: 1,
    rarity: 'common',
    imageUrl: '',
  },
  {
    id: 'starter-rare',
    title: 'Comet Tail',
    desc: 'A rare streak of light.',
    type: 'skill',
    category: 'starter',
    series: 's1',
    num: 2,
    rarity: 'rare',
    imageUrl: '',
  },
  {
    id: 'secret-legendary',
    title: 'Eclipse',
    desc: 'A legendary secret sticker.',
    type: 'secret',
    category: 'secret',
    series: 's1',
    num: 1,
    rarity: 'legendary',
    imageUrl: '',
  },
];

/** One seeded free daily wheel so the wheel UI spins offline. */
const SEED_WHEEL: WheelConfig = {
  wheelId: 'daily',
  cost: 0,
  cooldownMs: 24 * 60 * 60 * 1000,
  slots: [
    { index: 1, prizeType: 'tokens', qty: 5, weight: 4 },
    { index: 2, prizeType: 'xp', qty: 100, weight: 3 },
    { index: 3, prizeType: 'tokens', qty: 10, weight: 2 },
    { index: 4, prizeType: 'sticker', qty: 1, stickerId: 'starter-rare', weight: 1 },
  ],
};

/** Token prices per rarity for the OFFLINE shop (the server table lands in phase 4). */
const LOCAL_STICKER_PRICE: Record<Rarity, number> = {
  common: 25,
  uncommon: 50,
  rare: 100,
  epic: 200,
  legendary: 400,
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode etc.) — ignore */
  }
}

function makeUid(): string {
  // Avoid Math.random dependency assumptions — good enough for a local id.
  const rand = Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `local-${rand}${Date.now().toString(16)}`;
}

/** The local authoritative outcome of re-running a {@link RunRecord}. */
interface LocalOutcome {
  score: number;
  deathFamily: AttackFamily | null;
  durationMs: number;
}

/**
 * Re-run the recorded run through the pure core sim to get its authoritative
 * outcome (true online parity). While the P0 sim stub still throws, fall back to
 * scoring the last recorded sample's timestamp so offline play keeps working;
 * once {@link runSimulation} lands, this silently becomes exact.
 */
function authoritativeOutcome(record: RunRecord): LocalOutcome {
  try {
    const out = runSimulation(record);
    return { score: out.score, deathFamily: out.deathFamily, durationMs: out.durationMs };
  } catch {
    const lastMs = record.samples.length ? record.samples[record.samples.length - 1]!.tMs : 0;
    return { score: scoreFromElapsedMs(lastMs), deathFamily: null, durationMs: lastMs };
  }
}

export class LocalBackend implements GameServices {
  readonly mode: ServicesMode = 'local';

  /**
   * `named` for local dev so the FULL UI (profile/stickers/wheel) is reachable
   * offline. (The Firebase backend gates these behind a real named login; offline
   * there is no anon tease to enforce.)
   */
  readonly tier: AccountTier = 'named';

  private uid = '';

  // In-memory economy state (the offline stand-in for the Function-written
  // `users/{uid}` doc). Reset each session — offline progression isn't persisted
  // beyond the high score, by design.
  private xp = 0;
  private tokens = 100;
  /** Preferred UI language (offline stand-in for `users/{uid}.language`). */
  private language = 'en';
  private owned = new Set<string>();
  private equipped: { slot1: string | null; slot2: string | null } = { slot1: null, slot2: null };
  /** Offline subscription tier (drives the replay-star quota). */
  private profileTier: ProfileTier = 'free';
  /** Run ids the local user has SAVED to their curated collection (quota-bounded). */
  private saved: string[] = [];
  /** Run ids the local user has starred for their showcase card (subset of {@link saved}). */
  private starred: string[] = [];

  /**
   * Per-game PERSONAL stats roll-up (the offline stand-in for
   * `users/{uid}.gameStats`). Accumulated on every {@link submitRun} so the Stats →
   * Personal tab + the deaths donut reflect EVERY run, not just saved ones.
   */
  private gameStats: Partial<Record<GameId, PlayerGameStats>> = {};
  /**
   * Per-game NETWORK aggregate + its private recent-score window (the offline
   * stand-in for `stats/{gameId}`). Offline there's only you, so this tracks your
   * own runs — enough to exercise the Network tab.
   */
  private networkStats: Partial<Record<GameId, NetworkStats & { recentScores: number[] }>> = {};

  /**
   * In-memory replay store (the offline stand-in for `runs/{runId}`). Each
   * {@link submitRun} records the full {@link RunRecord} + a derived
   * {@link RunSummary} here so {@link getMyReplays}/{@link getReplay}/the star
   * flow have real data to operate on offline. Newest-first by insertion. Reset
   * each session (offline progression isn't persisted beyond the high score).
   */
  private replays: { id: string; gameId: GameId; record: RunRecord; summary: RunSummary }[] = [];
  /** Monotonic counter for fabricating local run ids. */
  private nextRunId = 1;

  /**
   * Offline anti-cheat / maintenance toggle (the stand-in for `config/runtime`).
   * Mutated by the `admin.setAntiCheat`/`admin.setMaintenance` no-ops so the admin
   * UI reflects its own changes within a session. Anti-cheat defaults ON.
   */
  private runtimeConfig: RuntimeConfig = { antiCheatEnabled: true, maintenance: false };

  async init(): Promise<{ uid: string }> {
    let uid = '';
    try {
      uid = localStorage.getItem(UID_KEY) ?? '';
    } catch {
      /* ignore */
    }
    if (!uid) {
      uid = makeUid();
      writeJson(UID_KEY, uid);
    }
    this.uid = uid;
    return { uid };
  }

  currentUser(): CurrentUser {
    return { uid: this.uid, name: 'You', email: 'you@local', tier: this.tier };
  }

  // --- account surface: no real auth offline ---------------------------------
  // Offline is already "named" (full UI). These are no-op stand-ins so the auth
  // screens can be exercised without a network.

  async signUp(_email: string, _password: string, name: string): Promise<CurrentUser> {
    void _email;
    void _password;
    return { uid: this.uid, name, email: 'you@local', tier: this.tier };
  }

  async logIn(_email: string, _password: string): Promise<CurrentUser> {
    void _email;
    void _password;
    return this.currentUser();
  }

  async signInWithGoogle(): Promise<CurrentUser> {
    // Offline is already "named" (full UI). No real Google flow — return the
    // current user so the auth screens are exercisable without a network.
    return this.currentUser();
  }

  async logOut(): Promise<void> {
    /* offline: nothing to sign out of */
  }

  async resetPassword(_email: string): Promise<void> {
    void _email;
    /* offline: no email to send */
  }

  async setLanguage(code: string): Promise<void> {
    // Offline parity: just remember it so getProfile reflects the choice this session.
    this.language = code;
  }

  // --- play + leaderboard (anon-allowed) -------------------------------------

  async submitRun(record: RunRecord, gameId: GameId = DEFAULT_GAME_ID): Promise<SubmitRunResult> {
    // Trusted offline: re-run the deterministic sim locally for the authoritative
    // outcome (same code the server uses), then update the PER-GAME board + best AND
    // record the run UNDER its `gameId` so the replay/showcase UI has real, game
    // -scoped data to drive (parity with the per-game online model).
    const { score, deathFamily, durationMs } = authoritativeOutcome(record);
    const best = await this.persistScore(score, gameId);

    // Reward (full offline tier): XP + tokens mirror the server policy (1 XP per
    // point, 1 token per 100 points). The after-game screen animates these, and a
    // level-up offers a wheel spin. XP is account-wide; stats below are per-game.
    const xpBefore = this.xp;
    const xpGained = score;
    const xpAfter = xpBefore + xpGained;
    const tokensGained = Math.floor(score / 100);
    const levelBefore = levelForXp(xpBefore).level;
    const levelAfter = levelForXp(xpAfter).level;
    this.xp = xpAfter;
    this.tokens += tokensGained;

    // Per-game PERSONAL stats — accumulate EVERY run (not just saved ones), which
    // is what makes the deaths donut populate; unlock any score achievements; fold
    // the run into the network aggregate too.
    const prevStats = this.gameStats[gameId] ?? emptyPlayerGameStats(gameId);
    const newly = newlyUnlockedAchievements(score, prevStats.unlockedAchievements);
    const isFirstRunForGame = prevStats.runs === 0;
    this.gameStats[gameId] = this.accumulatePersonal(prevStats, score, durationMs, deathFamily, newly);
    this.accumulateNetwork(gameId, score, newly, isFirstRunForGame);

    const reward: RunReward = {
      xpGained,
      xpBefore,
      xpAfter,
      levelBefore,
      levelAfter,
      leveledUp: levelAfter > levelBefore,
      tokensGained,
      unlockedAchievements: newly,
    };

    // Record the replay in-memory (newest first), mirroring `runs/{runId}` (a flat
    // store stamped with `gameId`; getMyReplays filters by it).
    const id = `local-run-${this.nextRunId++}`;
    const summary: RunSummary = {
      id,
      score,
      deathFamily,
      durationMs,
      createdAt: Date.now(),
      mode: record.mode,
    };
    this.replays.unshift({ id, gameId, record, summary });

    return { score, best, accepted: true, runId: id, reward };
  }

  async getNetworkStats(gameId: GameId = DEFAULT_GAME_ID): Promise<NetworkStats> {
    // Offline parity: project the in-memory aggregate (drop the private recent
    // -score window), or a zeroed snapshot before any run this session.
    const s = this.networkStats[gameId];
    if (!s) return emptyNetworkStats(gameId);
    const { recentScores: _recentScores, ...view } = s;
    void _recentScores;
    return { ...view };
  }

  async submitScore(score: number): Promise<number> {
    // @deprecated transitional shim — App.tsx still submits a raw score until it
    // migrates to submitRun(). Offline we trust it directly. No `gameId` param in
    // the spec, so it persists against the DEFAULT game.
    return this.persistScore(score, DEFAULT_GAME_ID);
  }

  async getLeaderboard(max = 10, gameId: GameId = DEFAULT_GAME_ID): Promise<ScoreEntry[]> {
    // Per-game board: the player's own rows live under a game-scoped key; the demo
    // SEED rows are shared (offline flavour only). Default game reuses the legacy key.
    const mine = readJson<ScoreEntry[]>(boardKeyFor(gameId), []);
    return [...SEED, ...mine].sort((a, b) => b.score - a.score).slice(0, max);
  }

  async getHighScore(gameId: GameId = DEFAULT_GAME_ID): Promise<number> {
    // Per-game personal best (default game reuses the legacy `barrage.highscore` key).
    return readJson<number>(highKeyFor(gameId), 0);
  }

  subscribeOnlineCount(cb: (count: number) => void): () => void {
    // Offline: it's just you.
    cb(1);
    return () => {};
  }

  // --- network / collection (offline in-memory parity) -----------------------

  async getProfile(): Promise<UserProfile> {
    const { level } = levelForXp(this.xp);
    // Lifetime counts by collectible class, resolved against the seed catalog
    // (mirrors the server's stickerCountField mapping).
    const countOf = (type: StickerCatalogItem['type']): number =>
      [...this.owned].filter((id) => SEED_CATALOG.find((s) => s.id === id)?.type === type)
        .length;
    return {
      uid: this.uid,
      name: 'You',
      language: this.language,
      createdAt: 0,
      xp: this.xp,
      level,
      tokens: this.tokens,
      tier: this.profileTier,
      savedReplayIds: [...this.saved],
      starredReplayIds: [...this.starred],
      skillStickerCount: countOf('skill'),
      seasonStickerCount: countOf('seasonal'),
      secretStickerCount: countOf('secret'),
      equipped: { ...this.equipped },
      gameStats: { ...this.gameStats },
    };
  }

  async getStickerCatalog(): Promise<StickerCatalogItem[]> {
    return SEED_CATALOG.map((s) => ({ ...s }));
  }

  async getOwnedStickers(): Promise<string[]> {
    return [...this.owned];
  }

  async equipSticker(slot: 1 | 2, stickerId: string): Promise<void> {
    if (!this.owned.has(stickerId)) {
      throw new Error(`equipSticker: sticker "${stickerId}" is not owned.`);
    }
    if (slot === 1) this.equipped.slot1 = stickerId;
    else this.equipped.slot2 = stickerId;
  }

  async spinWheel(wheelId: string): Promise<WheelResult> {
    if (wheelId !== SEED_WHEEL.wheelId) {
      throw new Error(`spinWheel: unknown wheel "${wheelId}".`);
    }
    if (this.tokens < SEED_WHEEL.cost) {
      throw new Error('spinWheel: not enough tokens.');
    }
    this.tokens -= SEED_WHEEL.cost;
    const slot = this.pickWheelSlot(SEED_WHEEL.slots);
    // Apply the grant locally (the server applies it atomically online).
    if (slot.prizeType === 'tokens') this.tokens += slot.qty;
    else if (slot.prizeType === 'xp') this.xp += slot.qty;
    else if (slot.prizeType === 'sticker' && slot.stickerId) this.owned.add(slot.stickerId);

    return {
      wheelId,
      slotIndex: slot.index,
      prizeType: slot.prizeType,
      qty: slot.qty,
      stickerId: slot.stickerId,
      tokensAfter: this.tokens,
    };
  }

  async buySticker(stickerId: string): Promise<void> {
    const item = SEED_CATALOG.find((s) => s.id === stickerId);
    if (!item) throw new Error(`buySticker: unknown sticker "${stickerId}".`);
    const price = LOCAL_STICKER_PRICE[item.rarity];
    if (this.tokens < price) throw new Error('buySticker: not enough tokens.');
    this.tokens -= price;
    this.owned.add(stickerId);
  }

  async getNews(): Promise<NewsItem[]> {
    return [
      {
        id: 'news-1',
        title: 'Welcome to Barrage (offline)',
        body: 'You are playing the local backend. Connect Firebase for the full network.',
        imageUrl: '',
        lang: 'en',
        order: 1,
        publishedAt: 0,
      },
    ];
  }

  async getActiveEvent(): Promise<EventDoc | null> {
    // Offline parity: no seeded events, so the Seasonal tab shows its "no event"
    // state. (Online, admins create `catalogEvents/*` docs to light it up.)
    return null;
  }

  async claimEvent(eventId: string): Promise<EventClaimResult> {
    // Offline parity: there are no events to claim (getActiveEvent returns null), so
    // a claim can't legitimately reach here. Fail honestly rather than mint a prize.
    throw new Error(`claimEvent: no active seasonal event offline ("${eventId}").`);
  }

  // --- replay + showcase (offline in-memory parity) -------------------------

  async getMyReplays(max?: number, gameId: GameId = DEFAULT_GAME_ID): Promise<RunSummary[]> {
    // Offline parity for the curated SAVED collection: return ONLY the runs the user
    // has SAVED (ids in `this.saved`), NOT every run played — mirroring the online
    // model where this resolves `users/{uid}.savedReplayIds` (the flat `runs` store
    // still holds every run for the leaderboard). Newest-saved-first (the `saved`
    // list is append-order, so reverse it), filtered to the requested game, then
    // clamped to `max` like the server's dispatcher.
    const summaries = [...this.saved]
      .reverse()
      .map((id) => this.replays.find((r) => r.id === id))
      .filter((r): r is NonNullable<typeof r> => !!r && r.gameId === gameId)
      .map((r) => ({ ...r.summary }));
    return max !== undefined ? summaries.slice(0, Math.max(0, max)) : summaries;
  }

  async getReplay(runId: string): Promise<RunRecord | null> {
    // Offline parity: return the recorded RunRecord for playback, or null.
    const entry = this.replays.find((r) => r.id === runId);
    return entry ? entry.record : null;
  }

  async saveReplay(runId: string): Promise<void> {
    // Offline parity for the curated SAVED collection: enforce the per-tier
    // savedReplayQuota against the in-memory saved set (the server enforces the same
    // on `users/{uid}.savedReplayIds`). The run must be a real recorded run, and a
    // SAVE is the prerequisite for a STAR. Idempotent: re-saving is a no-op.
    if (this.saved.includes(runId)) return;
    if (!this.replays.some((r) => r.id === runId)) {
      throw new Error(`saveReplay: unknown run "${runId}".`);
    }
    const quota = savedReplayQuota(this.profileTier);
    if (this.saved.length >= quota) {
      throw new Error(
        `saveReplay: saved-replay quota reached (${quota} for tier "${this.profileTier}").`,
      );
    }
    this.saved.push(runId);
  }

  async unsaveReplay(runId: string): Promise<void> {
    // Offline parity: drop the run from the SAVED set and cascade-unstar it (a
    // starred replay must remain saved — mirrors the server's UnsaveReplayResult,
    // which returns BOTH the saved + starred sets). Idempotent on unknown ids.
    this.saved = this.saved.filter((id) => id !== runId);
    this.starred = this.starred.filter((id) => id !== runId);
  }

  async setStarredReplay(runId: string): Promise<void> {
    // Offline parity: a run must be SAVED before it can be starred (the starred set
    // is a subset of the saved set — owner spec), then enforce the per-tier star
    // quota the server enforces.
    if (this.starred.includes(runId)) return;
    if (!this.saved.includes(runId)) {
      throw new Error(`setStarredReplay: run "${runId}" must be SAVED before it can be starred.`);
    }
    const quota = replayStarQuota(this.profileTier);
    if (this.starred.length >= quota) {
      throw new Error(`setStarredReplay: star quota reached (${quota} for tier "${this.profileTier}").`);
    }
    this.starred.push(runId);
  }

  async unstarReplay(runId: string): Promise<void> {
    this.starred = this.starred.filter((id) => id !== runId);
  }

  async getUserCard(uid: string): Promise<UserCard> {
    // Offline parity: synthesize a card from the in-memory profile, surfacing the
    // most-recently-starred replay (the same one the server features on cards/{uid}).
    const { level } = levelForXp(this.xp);
    const featuredId = this.starred.length ? this.starred[this.starred.length - 1] : null;
    const featured = featuredId
      ? this.replays.find((r) => r.id === featuredId)?.summary ?? null
      : null;
    return {
      uid,
      name: 'You',
      level,
      equippedStickerId: this.equipped.slot1,
      starred: featured ? { ...featured } : null,
    };
  }

  // --- admin (offline: full access so the admin UI is exercisable) -----------

  isAdmin(): boolean {
    // Offline dev exercises the FULL UI (mirrors the always-`named` tier), so the
    // admin panel is reachable. Online, the real `admin === true` claim gates it.
    return true;
  }

  async callAdmin(action: string, payload?: unknown): Promise<unknown> {
    // Offline parity: there's no server to apply admin actions against, so most are
    // no-ops. A few RETURN useful shapes so the admin dashboard renders offline:
    // `admin.getMetrics` → fabricated metrics; the runtime toggles → the mutated
    // config. Accepts both the bare verb and the namespaced `admin.*` form.
    const verb = action.startsWith('admin.') ? action.slice('admin.'.length) : action;
    switch (verb) {
      case 'getMetrics': {
        const metrics: AdminMetrics = {
          playersOnline: 1,
          runsToday: this.replays.length,
          totalUsers: 1,
          totalRuns: this.replays.length,
          flaggedRuns: 0,
          tokensInCirculation: this.tokens,
        };
        return metrics;
      }
      case 'setAntiCheat': {
        const p = (payload ?? {}) as { enabled?: boolean };
        if (typeof p.enabled === 'boolean') this.runtimeConfig.antiCheatEnabled = p.enabled;
        return { ...this.runtimeConfig } satisfies RuntimeConfig;
      }
      case 'setMaintenance': {
        const p = (payload ?? {}) as { enabled?: boolean };
        if (typeof p.enabled === 'boolean') this.runtimeConfig.maintenance = p.enabled;
        return { ...this.runtimeConfig } satisfies RuntimeConfig;
      }
      default:
        // Every other admin action: accept and no-op so the UI can be navigated;
        // echo the request so callers can confirm it round-tripped.
        return { action, payload, applied: false, offline: true };
    }
  }

  dispose(): void {
    /* nothing to tear down */
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  /**
   * Persist the player's best + their single best board row FOR `gameId`; returns
   * the new best. Best/board are namespaced per game (the default game reuses the
   * legacy keys), so each game keeps an independent personal best + row.
   */
  private async persistScore(score: number, gameId: GameId): Promise<number> {
    const best = Math.max(score, await this.getHighScore(gameId));
    writeJson(highKeyFor(gameId), best);

    const board = readJson<ScoreEntry[]>(boardKeyFor(gameId), []);
    board.push({ uid: this.uid, name: 'You', score, createdAt: Date.now() });
    // Keep only this player's best to avoid unbounded growth.
    const mine = board
      .filter((e) => e.uid === this.uid)
      .sort((a, b) => b.score - a.score)
      .slice(0, 1);
    writeJson(boardKeyFor(gameId), mine);
    return best;
  }

  /** Fold one run into a per-game personal stats aggregate (offline parity). */
  private accumulatePersonal(
    prev: PlayerGameStats,
    score: number,
    durationMs: number,
    deathFamily: AttackFamily | null,
    newly: readonly string[],
  ): PlayerGameStats {
    const deathsByFamily = { ...prev.deathsByFamily };
    if (deathFamily) deathsByFamily[deathFamily] = (deathsByFamily[deathFamily] ?? 0) + 1;
    return {
      gameId: prev.gameId,
      runs: prev.runs + 1,
      scoreSum: prev.scoreSum + score,
      bestScore: Math.max(prev.bestScore, score),
      longestMs: Math.max(prev.longestMs, durationMs),
      totalAvoided: prev.totalAvoided + Math.max(0, Math.floor(durationMs / 1000)),
      deathsByFamily,
      unlockedAchievements: [...prev.unlockedAchievements, ...newly],
      updatedAt: Date.now(),
    };
  }

  /** Fold one run into the in-memory network aggregate (offline parity). */
  private accumulateNetwork(
    gameId: GameId,
    score: number,
    newly: readonly string[],
    isFirstRunForGame: boolean,
  ): void {
    const prev = this.networkStats[gameId] ?? { ...emptyNetworkStats(gameId), recentScores: [] };
    const recentScores = [...prev.recentScores, score].slice(-NETWORK_RECENT_WINDOW);
    const sum = recentScores.reduce((a, b) => a + b, 0);
    const achievementCounts: Record<string, number> = { ...prev.achievementCounts };
    for (const id of newly) achievementCounts[id] = (achievementCounts[id] ?? 0) + 1;
    this.networkStats[gameId] = {
      gameId,
      totalRuns: prev.totalRuns + 1,
      bestScore: Math.max(prev.bestScore, score),
      recentAvgScore: recentScores.length ? Math.round(sum / recentScores.length) : 0,
      recentWindow: recentScores.length,
      playersTracked: prev.playersTracked + (isFirstRunForGame ? 1 : 0),
      achievementCounts,
      updatedAt: Date.now(),
      recentScores,
    };
  }

  /**
   * Weighted slot pick for the offline wheel. Uses `Math.random` (allowed in the
   * data layer — NOT core); the real server uses a server-side RNG so the client
   * can never bias the outcome.
   */
  private pickWheelSlot(slots: WheelSlot[]): WheelSlot {
    const total = slots.reduce((sum, s) => sum + s.weight, 0);
    let roll = Math.random() * total;
    for (const s of slots) {
      roll -= s.weight;
      if (roll <= 0) return s;
    }
    return slots[slots.length - 1]!;
  }
}
